import { Fragment } from "react";
import classNames from "classnames";
import { Menu, Transition } from "@headlessui/react";
import { ChevronDownIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import Button from "../../atomic/Button";
import { NavbarRendererVariant, type NavLinkProps } from "./types";
import { useNavbarContext } from "./NavbarContext";
import { filterVisibleItems } from "./utils";
import { NavbarRenderer } from "./NavbarRenderer";
import { menuPanelClassName } from "./Dropdown";

export function NavLink({
  item,
  variant = NavbarRendererVariant.Top,
  onNavigate
}: NavLinkProps) {
  const { navigateFromSource, isRouteActive } = useNavbarContext();
  const { label, source } = item;
  const isActive = isRouteActive(source);
  const children = filterVisibleItems(item.children ?? []);
  const hasChildren = children.length > 0;

  const handleClick = () => {
    navigateFromSource(source);
    onNavigate?.();
  };

  const baseStyles: React.CSSProperties = {
    color: isActive ? "var(--navbar-text-active)" : "var(--navbar-text)",
    borderColor: "var(--navbar-border)"
  };

  if (variant === NavbarRendererVariant.Menu) {
    const menuButton = (
      <Button
        onClick={handleClick}
        className={classNames(
          "block w-full rounded-none border-0 !bg-white px-4 py-2 !font-normal text-left text-sm text-gray-300 shadow-none hover:!bg-gray-100",
          isActive && "!bg-gray-100 text-gray-600 !font-normal"
        )}
        pxFour={false}
      >
        {label}
      </Button>
    );

    if (!hasChildren) return menuButton;

    return (
      <div className="group relative w-full">
        <div className="flex w-full items-center justify-between">
          {menuButton}
          <ChevronRightIcon
            className="pointer-events-none absolute right-4 h-4 w-4 shrink-0 -rotate-90 text-gray-700"
            aria-hidden="true"
          />
        </div>
        <div className="invisible absolute left-full top-0 z-[60] min-w-[14rem] rounded-md bg-white py-1 text-gray-700 opacity-0 shadow-lg ring-1 ring-black ring-opacity-5 transition-all duration-100 group-hover:visible group-hover:opacity-100">
          {children.map((child) => (
            <NavbarRenderer
              key={child.id}
              item={child}
              variant={NavbarRendererVariant.Menu}
              onNavigate={onNavigate}
            />
          ))}
        </div>
      </div>
    );
  }

  if (variant === NavbarRendererVariant.Mobile) {
    const mobileButton = (
      <Button
        onClick={handleClick}
        className="block w-full rounded-lg border-0 !bg-transparent px-4 py-2 text-left text-sm font-medium shadow-none"
        style={baseStyles}
        onMouseEnter={(e) => {
          if (!isActive) {
            e.currentTarget.style.color = "var(--navbar-text-hover)";
          }
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.color = isActive
            ? "var(--navbar-text-active)"
            : "var(--navbar-text)";
        }}
        pxFour={false}
      >
        {label}
      </Button>
    );

    if (!hasChildren) return mobileButton;

    return (
      <div className="py-1">
        {mobileButton}
        <div className="pl-4">
          {children.map((child) => (
            <NavbarRenderer
              key={child.id}
              item={child}
              variant={NavbarRendererVariant.Mobile}
              onNavigate={onNavigate}
            />
          ))}
        </div>
      </div>
    );
  }

  const linkClassName = classNames(
    "block h-min rounded-lg border-0 !bg-transparent px-3 py-2 text-center align-middle text-sm font-medium shadow-none",
    isActive && "font-semibold"
  );

  const handleHoverEnter = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!isActive) {
      e.currentTarget.style.color = "var(--navbar-text-hover)";
    }
  };

  const handleHoverLeave = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.currentTarget.style.color = isActive
      ? "var(--navbar-text-active)"
      : "var(--navbar-text)";
  };

  if (!hasChildren) {
    return (
      <Button
        onClick={handleClick}
        className={linkClassName}
        style={baseStyles}
        onMouseEnter={handleHoverEnter}
        onMouseLeave={handleHoverLeave}
        pxFour={false}
      >
        {label}
      </Button>
    );
  }

  // Same Menu/Transition/panel styling as the Products-style Dropdown, so
  // My Queue's program-type options look and behave the same way — only
  // the chevron toggles the menu, the label itself still navigates.
  return (
    <Menu as="div" className="relative inline-flex items-center">
      <Button
        onClick={handleClick}
        className={classNames(linkClassName, "!pr-1")}
        style={baseStyles}
        onMouseEnter={handleHoverEnter}
        onMouseLeave={handleHoverLeave}
        pxFour={false}
      >
        {label}
      </Button>
      <Menu.Button
        as={Button}
        className="inline-flex items-center justify-center rounded-lg border-0 !bg-transparent px-1 py-2 shadow-none focus:outline-none"
        style={{ color: "var(--navbar-text)" }}
        aria-label={`${label ?? "Menu"} options`}
        onMouseEnter={(e) => {
          e.currentTarget.style.color = "var(--navbar-text-hover)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.color = "var(--navbar-text)";
        }}
      >
        <ChevronDownIcon className="h-4 w-4" aria-hidden="true" />
      </Menu.Button>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items
          className={classNames(
            "absolute left-0 top-full z-50 mt-1 w-56 origin-top-left focus:outline-none",
            menuPanelClassName
          )}
        >
          {children.map((child) => (
            <NavbarRenderer
              key={child.id}
              item={child}
              variant={NavbarRendererVariant.Menu}
              onNavigate={onNavigate}
            />
          ))}
        </Menu.Items>
      </Transition>
    </Menu>
  );
}
